import { ConfigService } from '@stockcare-services/config-service/config.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ITheatrePatientVisit } from '@stockcare-models/ITheatrePatientVisit';
import { TheatrePatientStorageService } from '@stockcare-storage/theatre-patient/theatre-patient-storage.service';
import { QueueType } from '@stockcare-models/QueueType';
import { QueueService } from '@stockcare-services/queue-service/queue.service';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { take } from 'rxjs/operators';
import { TheatrePatient } from '@stockcare-storage/theatre-patient/theatre-patient';
import { AuthenticationService } from '..';

@Injectable({
  providedIn: 'root',
})
export class TheatrePatientService {
  constructor(
    private http: HttpClient,
    private conf: ConfigService,
    private theatrePatientStorageService: TheatrePatientStorageService,
    private queueService: QueueService,
    private deviceService: DeviceService,
    private authService: AuthenticationService
  ) {}

  ensurePatientsLoaded(physicalLocationId: number): Observable<boolean> {
    return new Observable((observer) => {
      this.getAllTheatrePatientsByPhysicalLocation(physicalLocationId).subscribe(
        () => {
          observer.next(true);
        },
        () => {
          observer.next(false);
        }
      );
    });
  }

  ensurePatientLoaded(theatreVisitId: number): Observable<boolean> {
    return new Observable((observer) => {
      this.getTheatrePatientAndLoad(theatreVisitId).subscribe(
        () => {
          observer.next(true);
        },
        () => {
          observer.next(false);
        }
      );
    });
  }

  getTheatrePatientAndLoad(theatreVisitId: number) {
    return new Observable((observer) => {
      this.http
        .get<ITheatrePatientVisit>(`${this.conf.config.baseUrl}/gettheatrepatient/${theatreVisitId}`)
        .subscribe(
          (patient) => {
            this.theatrePatientStorageService
              .addUpdateSinglePatient(patient)
              .subscribe(
                () => {
                  observer.next(patient);
                },
                (error) => {
                  observer.error(error);
                }
              );
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  getAllTheatrePatientsByPhysicalLocation(physicalLocationId: number) {
    return new Observable((observer) => {
      this.http
        .get<ITheatrePatientVisit[]>(`${this.conf.config.baseUrl}/patienttheatre/GetAllByPhysicalLocation/${physicalLocationId}`)
        .pipe(take(1))
        .subscribe(
          (patients) => {
            this.theatrePatientStorageService
              .addUpdatePatientDb(patients)
              .pipe(take(1))
              .subscribe(
                () => {
                  observer.next(patients);
                },
                (error) => {
                  observer.error(error);
                }
              );
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  getTheatreVisitHistory(physicalLocationId: number) {
    return this.http.get<ITheatrePatientVisit[]>(`${this.conf.config.baseUrl}/patienttheatre/GetTheatreVisitHistory/${physicalLocationId}`);
  }

  moveToTheatreWaiting(patientId: number, physicalLocationId: number, institutionId: number, procedureTypeId): Observable<any> {
    return this.http.post<any>(
      `${
        this.conf.config.baseUrl
      }/patienttheatre/movetotheatrewaiting/id/${patientId}/${physicalLocationId}/${institutionId}/${procedureTypeId}/${this.deviceService.getDeviceId()}`,
      {}
    );
  }

  createTheatreVisitAndMoveToOperatingRoom(
    patientId: number,
    physicalLocationId: number,
    institutionId: number,
    procedureTypeId
  ): Observable<number> {
    return this.http.post<number>(
      `${
        this.conf.config.baseUrl
      }/patienttheatre/createTheatreVisitAndMoveToOperatingRoom/id/${patientId}/${physicalLocationId}/${institutionId}/${procedureTypeId}/${this.deviceService.getDeviceId()}/${this.deviceService.getPhysicalLocationRoom()}`,
      {}
    );
  }

  moveToTheatreOperatingRoom(theatrePatient: ITheatrePatientVisit): Observable<any> {
    return this.http.post<ITheatrePatientVisit>(`${this.conf.config.baseUrl}/patienttheatre/movetotheatreoperatingroom`, theatrePatient);
  }

  changePatientOperatingRoom(patientId: number, operatingRoomId: number): Observable<any> {
    let deviceId = this.deviceService.getDeviceId();
    var changeOperatingRoomRequest = {
      "patientTheatreId": patientId,
      "physicalLocationOperatingRoomId": operatingRoomId,
      "deviceId": deviceId
    }

    return this.http.post<any>(`${this.conf.config.baseUrl}/patienttheatre/changeOperatingRoom`, changeOperatingRoomRequest);
  }

  cancelPatientVisit(patientId: number, deviceId: number): Observable<any> {
    var cancelVisitRequest = {
      "patientTheatreId": patientId,
      "deviceId": deviceId
    }

    return this.http.post<any>(`${this.conf.config.baseUrl}/patienttheatre/cancelTheatreVisit`, cancelVisitRequest);
  }

  scanOutAndDelaySubmission(theatrePatientVisit: ITheatrePatientVisit): Observable<any> {
    return new Observable((observer) => {

      var operatingEndDate = theatrePatientVisit.operatingEndDate.toString()

      if (operatingEndDate == '0001-01-01T00:00:00') {
        theatrePatientVisit.operatingEndDate = new Date();
      }

      this.queueService.enqueue(QueueType.CompleteDeferredTheatreVisitQueue, theatrePatientVisit).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  checkIfProcedureIsInProgress(patientId: Number): Observable<any> {
    return this.http.get(`${this.conf.config.baseUrl}/PatientTheatre/GetIsProcedureInProgress/${patientId}`);
  }

  checkIfOperatingRoomIsOccupied(physicalLocationOperatingRoomId: Number): Observable<any> {
    return this.http.get(`${this.conf.config.baseUrl}/PatientTheatre/GetIsTheatreRoomOccupied/${physicalLocationOperatingRoomId}`);
  }

  updateStatusProcedureInProgress(theatrePatientVisit: ITheatrePatientVisit) {
    return new Observable((observer) => {
      this.queueService.enqueue(QueueType.UpdateToProcedureInProgress, theatrePatientVisit).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  startTimerAndMoveToOperatingRoom(theatrePatientVisit: ITheatrePatientVisit): Observable<any> {
    theatrePatientVisit.materials = theatrePatientVisit.materials.filter((x) => x.quantity > 0);

    theatrePatientVisit.operatingStartDate = new Date();

    return new Observable((observer) => {
      this.queueService.enqueue(QueueType.TheatreStartTimerAndMoveToOperatingRoomQueue, theatrePatientVisit).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  updateStatusProcedureComplete(theatrePatientVisit: ITheatrePatientVisit) {
    return new Observable((observer) => {
      this.queueService.enqueue(QueueType.UpdateToProcedureComplete, theatrePatientVisit).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  submit(theatrePatientVisit: ITheatrePatientVisit): Observable<any> {
    theatrePatientVisit.materials = theatrePatientVisit.materials.filter((x) => x.quantity > 0);

    return new Observable((observer) => {
      this.queueService.enqueue(QueueType.TheatreVisitSubmitQueue, theatrePatientVisit).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  stopTimerAndSubmit(theatrePatientVisit: ITheatrePatientVisit): Observable<any> {
    theatrePatientVisit.materials = theatrePatientVisit.materials.filter((x) => x.quantity > 0);

    theatrePatientVisit.operatingEndDate = new Date();

    return new Observable((observer) => {
      this.queueService.enqueue(QueueType.TheatreStopTimerAndScanOutQueue, theatrePatientVisit).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  stopTimerAndMoveToRecovery(theatrePatientVisit: ITheatrePatientVisit): Observable<any> {
    theatrePatientVisit.materials = theatrePatientVisit.materials.filter((x) => x.quantity > 0);

    theatrePatientVisit.operatingEndDate = new Date();

    return new Observable((observer) => {
      this.queueService.enqueue(QueueType.TheatreStopTimerAndMoveToRecoveryQueue, theatrePatientVisit).subscribe(
        () => {
          observer.next(true);
        },
        (error) => {
          observer.error(false);
        }
      );
    });
  }

  update(theatrePatientVisit: ITheatrePatientVisit): Observable<any> {
    theatrePatientVisit.materials = theatrePatientVisit.materials.filter((x) => x.quantity > 0);
    theatrePatientVisit.clientTimestamp = new Date();

    return new Observable((observer) => {
      this.queueService
        .enqueue(QueueType.TheatreUpdateQueue, theatrePatientVisit)
        .pipe(take(1))
        .subscribe(
          () => {
            observer.next(true);
          },
          (error) => {
            observer.error(false);
          }
        );
    });
  }

  upgradeToMajorProcedure(patientTheatreVisitId: number): Observable<any> {
    return this.http.post<any>(`${this.conf.config.baseUrl}/patienttheatre/upgradeToMajorProcedure/${patientTheatreVisitId}`, {});
  }

  getTheatrePatient(patientTheatreVisitId: number): Observable<any> {
    return this.http.get<any>(`${this.conf.config.baseUrl}/patienttheatre/gettheatrepatient/${patientTheatreVisitId}`, {});
  }
}
